import { useCallback, useEffect, useState } from "react";
import { getPricebookDiff, readPricebookDraftRefusal } from "../../../../services";
import { PricebookDraftDiff } from "../../../../types/price-book.type";

const DIFF_LOAD_FAILED = "อ่านความต่างจากสมุดราคาต้นฉบับไม่สำเร็จ กรุณาลองอีกครั้ง";

export interface PricebookDraftDiffState {
  /** Null until read, when disabled, or when the read was refused. */
  diff: PricebookDraftDiff | null;
  loading: boolean;
  /** Why the comparison could not be read (e.g. the source was deleted); empty otherwise. */
  errorMessage: string;
  reload: () => void;
}

/**
 * A draft compared with its source: the draft bar's steps, the apply button and the apply
 * dialog all read this one answer. `version` is the draft's `updated_at`, so every save on the
 * draft reads it again; `reload` is for changes made elsewhere (test shops, the source).
 */
export function usePricebookDraftDiff(
  draftUuid: string,
  enabled: boolean,
  version: string,
): PricebookDraftDiffState {
  const [diff, setDiff] = useState<PricebookDraftDiff | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [reloadCount, setReloadCount] = useState<number>(0);

  useEffect(() => {
    setErrorMessage("");
    if (!enabled) {
      setDiff(null);
      setLoading(false);
      return;
    }
    // Moving to another book, or reading again, makes an in-flight answer stale.
    let ignore = false;
    setLoading(true);

    const load = async () => {
      try {
        const {
          data: { data },
        } = await getPricebookDiff(draftUuid);
        if (!ignore) setDiff(data);
      } catch (error) {
        if (ignore) return;
        setDiff(null);
        setErrorMessage(readPricebookDraftRefusal(error)?.message ?? DIFF_LOAD_FAILED);
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    load();
    return () => {
      ignore = true;
    };
  }, [draftUuid, enabled, version, reloadCount]);

  const reload = useCallback(() => setReloadCount((count) => count + 1), []);

  return { diff, loading, errorMessage, reload };
}
